/**
 * ReportContextCard — the L1A report-context split (clean-flow §6, Prompt 4).
 *
 * The report-context (presenting concerns · property address · verdict · room
 * allocation) used to live inside StaffTechnicalPanel's report-context Box. For
 * the L1A flow it is split across two surfaces:
 *   · VerdictField        → Step 5 (the verdict IS the verdict).
 *   · ReportContextRest   → the "more" drawer (concerns · address · room overrides).
 *
 * Each loads the same intake doc but edits + SAVES only its OWN leaves (disjoint
 * saves saveVerdict / saveReportContextRest — writeIntakeDoc deep-merges, so the
 * two never clobber). StaffTechnicalPanel's own card is suppressed via
 * `hideReportContext` so there is exactly one editor per field. Reuse-first: same
 * loader + the same ReportContextForm shape the panel used.
 */
import { useEffect, useId, useState } from 'react';
import { Box, Btn3 } from '../ui';
import {
  loadPropertiesIntake,
  saveVerdict,
  saveRecommendedProperty,
  saveReportContextRest,
  blankReportContext,
  normaliseVerdictStatus,
  VALID_BEDROOM_ZONES,
  type ReportContextForm,
  type RoomOverrideRow,
} from '../../lib/staffIntake';
import { floorHasBoundary, floorHasEntrance } from '../../lib/candidateGrid';
import { summarizeVerdict } from '../../lib/verdictDraft';

const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--ink-soft)',
  marginBottom: 4,
};
const field: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  boxSizing: 'border-box',
};

interface Common {
  clientId: string | null;
  projectId: string;
  intakeTier?: string;
  actor: { uid: string | null; via: 'staff' };
  onMsg: (m: string) => void;
}

/** Shared loader → the report-context form + a flat floor list for the room
 *  dropdown. Returns null while loading. */
function useReportContext(clientId: string | null, projectId: string, intakeTier?: string) {
  const [ctx, setCtx] = useState<ReportContextForm | null>(null);
  const [floors, setFloors] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (!clientId) {
      setCtx(null);
      return;
    }
    let alive = true;
    loadPropertiesIntake({ clientId, projectId }, intakeTier)
      .then((r) => {
        if (!alive) return;
        setCtx(r.reportContext || blankReportContext());
        const fl: Array<{ id: string; name: string }> = [];
        for (const p of r.form.properties || []) {
          for (const f of p.floors || []) fl.push({ id: f.id, name: f.name || f.id });
        }
        setFloors(fl);
      })
      .catch(() => alive && setCtx(blankReportContext()));
    return () => {
      alive = false;
    };
  }, [clientId, projectId, intakeTier]);
  return { ctx, setCtx, floors };
}

/* ─────────── The §C/§D recommended-property control (ONE shared component) ───────────
 * §D cross-property winner — MOVED to the Verdict screen from Evaluate (2026-07-02),
 * then LIFTED out of VerdictField into this shared component (2026-07-03) so the
 * Compare tab (§C winner strip) mounts the SAME REAL control — never a lookalike
 * (L1A shared language §4: "reuse" = mount the real component).
 *
 * Reads/writes the SAME field `answers.recommended_property_id`. The candidate
 * list keys the winner on the STABLE registry `propertyId` (never the churn-prone
 * candidate slot id); only joined candidates (those with a propertyId) are
 * offerable. Loaded on mount; persisted via the disjoint saveRecommendedProperty
 * (deep-merges the one leaf — never clobbers floors/properties/verdict text).
 * Renders nothing without a client or for a single-candidate engagement (no
 * cross-property choice) — exactly the gate VerdictField applied inline. */
export function RecommendedPropertyPicker({
  clientId,
  projectId,
  intakeTier,
  actor,
  onMsg,
  onChanged,
  helper = 'Which candidate the report recommends. The verdict text itself is typed below.',
}: Common & {
  /** Optional live echo of the (optimistically) saved pick — lets a host
   *  surface (the Compare winner strip) mirror the staff pick without a
   *  second loader. Additive; VerdictField doesn't pass it. */
  onChanged?: (propertyId: string) => void;
  /** Context line under the select. Defaults to the Verdict-screen wording;
   *  the Compare tab passes its own (the "typed below" clause is Verdict-only). */
  helper?: string;
}) {
  // Unique per mount (the Verdict screen and the Compare winner strip each
  // mount their own instance) — keeps the label htmlFor binding collision-free.
  const selectId = useId();
  const [cands, setCands] = useState<Array<{ id: string; propertyId?: string; label: string }>>([]);
  const [recId, setRecId] = useState('');
  const [savingRec, setSavingRec] = useState(false);
  useEffect(() => {
    if (!clientId) {
      setCands([]);
      setRecId('');
      return;
    }
    let alive = true;
    loadPropertiesIntake({ clientId, projectId }, intakeTier)
      .then((r) => {
        if (!alive) return;
        setRecId(r.recommendedPropertyId || '');
        setCands(
          (r.form.properties || []).map((p, i) => ({
            id: p.id,
            propertyId: p.propertyId,
            label: p.label?.trim() || `Property ${String.fromCharCode(65 + i)}`,
          })),
        );
      })
      .catch(() => {
        if (alive) {
          setCands([]);
          setRecId('');
        }
      });
    return () => {
      alive = false;
    };
  }, [clientId, projectId, intakeTier]);

  async function onChangeRecommended(next: string) {
    if (!clientId) return;
    setRecId(next); // optimistic
    onChanged?.(next);
    setSavingRec(true);
    try {
      await saveRecommendedProperty({ clientId, projectId }, next, actor, intakeTier);
      onMsg(next ? 'Recommended property saved.' : 'Recommended property cleared.');
    } catch (e) {
      onMsg(`Recommended property save failed: ${(e as Error).message}`);
    } finally {
      setSavingRec(false);
    }
  }

  // Only shown for a multi-candidate engagement (a single property has no
  // cross-property choice) — the same `cands.length > 1` gate VerdictField used.
  if (!clientId || cands.length <= 1) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      {/* htmlFor/id binding (Vera gate on cb6fd19, Finding 4) — programmatic
          label→control association + click-to-focus. The aria-label stays (it
          wins as the accessible name and carries the fuller wording). */}
      <label style={lbl} htmlFor={selectId}>
        Recommended property (§D cross-property verdict)
      </label>
      <select
        id={selectId}
        style={field}
        value={recId}
        disabled={savingRec}
        onChange={(e) => onChangeRecommended(e.target.value)}
        aria-label="Recommended property — the §D cross-property winner"
      >
        <option value="">— set at curation —</option>
        {cands.map((c) =>
          c.propertyId ? (
            <option key={c.id} value={c.propertyId}>
              {c.label}
            </option>
          ) : null,
        )}
      </select>
      <p style={{ fontSize: 11, color: 'var(--ink-mute)', margin: '6px 0 0' }}>
        {helper}
      </p>
    </div>
  );
}

/* ───────────────────────── Step 5 — VERDICT ───────────────────────── */
export function VerdictField({
  clientId,
  projectId,
  intakeTier,
  actor,
  onMsg,
  onRecommendationChanged,
}: Common & {
  /** Live echo of the saved recommended-property pick UP to the host page
   *  (owner 2026-07-03: the Compare stage chip ticks without a reload).
   *  Additive — hosts that don't care simply omit it. */
  onRecommendationChanged?: (propertyId: string) => void;
}) {
  const { ctx, setCtx } = useReportContext(clientId, projectId, intakeTier);
  const [saving, setSaving] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  if (!clientId) {
    return (
      <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
        Link a client to write the verdict.
      </p>
    );
  }
  if (!ctx) return <p style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Loading verdict…</p>;

  const patch = (p: Partial<ReportContextForm>) => setCtx((c) => (c ? { ...c, ...p } : c));

  async function save() {
    if (!ctx || !clientId) return;
    setSaving(true);
    try {
      await saveVerdict(
        { clientId, projectId },
        {
          verdict_text: ctx.verdict_text,
          verdict_reasoning_text: ctx.verdict_reasoning_text,
          // 2.2 — the global status DROPDOWN is gone (per-property recommendation is
          // now the source of the §D status). We still persist the loaded value
          // verbatim so dropping the UI never clobbers an existing stored chip; the
          // cross-candidate status synthesis is deferred to the functions repo.
          verdict_status: ctx.verdict_status,
        },
        actor,
        intakeTier,
      );
      onMsg('Verdict saved.');
    } catch (e) {
      onMsg(`Verdict save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  // 2.2 — SUMMARIZE: synthesise the single project-level §D verdict TEXT from the
  // per-property recommendations + the winner, into the editable statement field.
  // Reuses the same LOCAL draft plumbing as the per-property "AI draft" (no portal
  // AI endpoint exists — see verdictDraft.ts). Staff edit the result, then Save.
  // TODO(pass2-functions): the DEPLOYED report's cross-candidate synthesis +
  // comparative verdictRow rendering live in the `functions` repo
  // (l1aTemplate.js::autoDraftVerdict → cross-candidate, buildCandidates.js,
  // verdictRow @ l1aTemplate.js:1288). That repo is on an unrelated branch and is
  // OUT OF SCOPE here; this button is the portal-side hand-off that seeds the text.
  async function summarize() {
    if (!clientId) return;
    setSummarizing(true);
    try {
      const r = await loadPropertiesIntake({ clientId, projectId }, intakeTier);
      const rec = r.recommendedPropertyId || '';
      const cands = (r.form.properties || []).map((p, i) => {
        const floors = Array.isArray(p.floors) ? p.floors : [];
        return {
          label: p.label?.trim() || `Property ${String.fromCharCode(65 + i)}`,
          status: normaliseVerdictStatus(p.recommendation_status),
          reasoning: p.recommendation_reasoning,
          isWinner: !!rec && (rec === p.propertyId || rec === p.id),
          ready: floors.some(floorHasBoundary) && floors.some(floorHasEntrance),
        };
      });
      const text = summarizeVerdict(cands);
      if (!text) {
        onMsg('No candidate properties to summarize yet.');
        return;
      }
      patch({ verdict_text: text });
      onMsg('Drafted the §D verdict from the per-property recommendations — edit, then Save.');
    } catch (e) {
      onMsg(`Summarize failed: ${(e as Error).message}`);
    } finally {
      setSummarizing(false);
    }
  }

  return (
    <div>
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 12px', lineHeight: 1.55 }}>
        The overall call across the Vastu · Astro-Vastu · remedy-load lenses — renders inside the
        report’s Section D. <b>Summarize</b> drafts it from each property’s recommendation (set in the
        Evaluate stage); edit it freely. Leave blank to keep the “to be completed at curation”
        placeholder.
      </p>
      {/* §D cross-property winner — relocated from the Evaluate screen (2026-07-02),
          now the SHARED RecommendedPropertyPicker (also mounted by the Compare
          tab's winner strip — one real control, one write path). It self-gates:
          nothing renders for a single-candidate engagement. */}
      <RecommendedPropertyPicker
        clientId={clientId}
        projectId={projectId}
        intakeTier={intakeTier}
        actor={actor}
        onMsg={onMsg}
        onChanged={onRecommendationChanged}
      />
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 4,
          }}
        >
          <label style={{ ...lbl, marginBottom: 0 }}>Verdict statement</label>
          <Btn3 intent="ghost" size="sm" onClick={summarize} disabled={summarizing}>
            {summarizing ? 'Summarizing…' : '✦ Summarize across properties'}
          </Btn3>
        </div>
        <textarea
          style={{ ...field, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }}
          value={ctx.verdict_text}
          onChange={(e) => patch({ verdict_text: e.target.value })}
          placeholder="The headline recommendation — short. 1–3 lines; the lead line of Section D. Or press Summarize."
          aria-label="Verdict statement"
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>Reasoning</label>
        <textarea
          style={{ ...field, minHeight: 100, resize: 'vertical', lineHeight: 1.5 }}
          value={ctx.verdict_reasoning_text}
          onChange={(e) => patch({ verdict_reasoning_text: e.target.value })}
          placeholder="The cross-lens reasoning — one paragraph per line."
          aria-label="Verdict reasoning"
        />
      </div>
      <Btn3 intent="grn" size="sm" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save verdict'}
      </Btn3>
    </div>
  );
}

/* ──────────────── "more" drawer — REST of report-context ──────────────── */
export function ReportContextRest({ clientId, projectId, intakeTier, actor, onMsg }: Common) {
  const { ctx, setCtx, floors } = useReportContext(clientId, projectId, intakeTier);
  const [saving, setSaving] = useState(false);

  if (!clientId) return null;
  if (!ctx) {
    return (
      <Box title="Report context (staff)">
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', margin: 0 }}>Loading…</p>
      </Box>
    );
  }

  const patch = (p: Partial<ReportContextForm>) => setCtx((c) => (c ? { ...c, ...p } : c));
  const patchRow = (i: number, p: Partial<RoomOverrideRow>) =>
    setCtx((c) =>
      c ? { ...c, room_overrides: c.room_overrides.map((r, j) => (j === i ? { ...r, ...p } : r)) } : c,
    );
  const addRow = () =>
    setCtx((c) =>
      c ? { ...c, room_overrides: [...c.room_overrides, { member: '', zone: '', floorId: '' }] } : c,
    );
  const removeRow = (i: number) =>
    setCtx((c) => (c ? { ...c, room_overrides: c.room_overrides.filter((_, j) => j !== i) } : c));

  async function save() {
    if (!ctx || !clientId) return;
    setSaving(true);
    try {
      await saveReportContextRest(
        { clientId, projectId },
        {
          presenting_concerns_text: ctx.presenting_concerns_text,
          property_address: ctx.property_address,
          room_overrides: ctx.room_overrides,
        },
        actor,
        intakeTier,
      );
      onMsg('Report context saved.');
    } catch (e) {
      onMsg(`Report context save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      title="Report context (staff)"
      note="Presenting concerns · property address · room allocation — printed on the report (the verdict is in Step 5)"
    >
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Presenting concerns</label>
        <textarea
          style={{ ...field, minHeight: 90, resize: 'vertical', lineHeight: 1.5 }}
          value={ctx.presenting_concerns_text}
          onChange={(e) => patch({ presenting_concerns_text: e.target.value })}
          placeholder={'The client’s stated problems / goals — one per line.'}
          aria-label="Presenting concerns, one per line"
        />
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
          One concern per line — opens the report (Section A).
        </p>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Property address</label>
        <input
          style={field}
          value={ctx.property_address}
          onChange={(e) => patch({ property_address: e.target.value })}
          placeholder="e.g. 12 Vijay Nagar, Indore, MP 452010"
          aria-label="Property address"
        />
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
          Printed on the cover; blank → derives from the site location.
        </p>
      </div>
      <div style={{ paddingTop: 12, borderTop: '1px dashed var(--rule)' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
          Room allocation (curation)
        </div>
        {ctx.room_overrides.length === 0 ? (
          <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 10px' }}>
            No overrides — the engine allocates rooms (couple shares one bedroom).
          </p>
        ) : (
          ctx.room_overrides.map((row, i) => (
            <div
              key={i}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}
            >
              <div style={{ flex: '1 1 150px', minWidth: 130 }}>
                <label style={lbl}>Member (or “couple”)</label>
                <input
                  style={field}
                  value={row.member}
                  onChange={(e) => patchRow(i, { member: e.target.value })}
                  placeholder="e.g. couple · Tarveen"
                />
              </div>
              <div style={{ flex: '0 1 120px', minWidth: 100 }}>
                <label style={lbl}>Bedroom zone</label>
                <select style={field} value={row.zone} onChange={(e) => patchRow(i, { zone: e.target.value })}>
                  <option value="">— zone —</option>
                  {VALID_BEDROOM_ZONES.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '0 1 140px', minWidth: 110 }}>
                <label style={lbl}>Floor (optional)</label>
                <select style={field} value={row.floorId} onChange={(e) => patchRow(i, { floorId: e.target.value })}>
                  <option value="">— any marked —</option>
                  {floors.map((fl) => (
                    <option key={fl.id} value={fl.id}>{fl.name}</option>
                  ))}
                </select>
              </div>
              <Btn3 intent="ghost" size="sm" onClick={() => removeRow(i)}>Remove</Btn3>
            </div>
          ))
        )}
        <Btn3 intent="ghost" size="sm" onClick={addRow}>+ Add room override</Btn3>
      </div>
      <div style={{ marginTop: 14 }}>
        <Btn3 intent="grn" size="sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save report context'}
        </Btn3>
      </div>
    </Box>
  );
}
